import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';
import { useReactiveSEO, useReactiveBranding, useReactiveSite, getSiteUrl, loadSchema } from '@/hooks/useReactiveSettings';

interface SEOHeadProps {
  title?: string;
  description?: string;
  path?: string;
  type?: 'website' | 'article';
  pageName?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  path = '/',
  type = 'website',
  pageName = 'index',
}) => {
  const { t, language, getLocalizedPath } = useLanguage();
  const seoSettings = useReactiveSEO();
  const brandingSettings = useReactiveBranding();
  const siteSettings = useReactiveSite();
  const schemaSettings = loadSchema();

  // Получаем URL сайта из настроек или используем текущий origin
  const siteUrl = siteSettings.siteUrl || getSiteUrl();

  const pageTitle = title || t.siteTitle;
  const pageDescription = description || t.siteDescription;
  const canonicalUrl = `${siteUrl}${getLocalizedPath(path)}`;
  const ogImageUrl = seoSettings?.ogImageUrl 
    ? (seoSettings.ogImageUrl.startsWith('http') ? seoSettings.ogImageUrl : `${siteUrl}${seoSettings.ogImageUrl}`)
    : `${siteUrl}/og-image.jpg`;
  const faviconUrl = brandingSettings?.faviconUrl || '/favicon.ico';

  // Определяем правильный lang код для HTML
  const htmlLang = language === 'ua' ? 'uk' : language;
  const ogLocale = language === 'ua' ? 'uk_UA' : language === 'ru' ? 'ru_RU' : 'en_US';

  // Данные Schema.org для текущего языка
  const webAppSchema = schemaSettings.webApplication[language];
  const orgSchema = schemaSettings.organization[language];

  // Прямое обновление DOM для гарантированного применения
  useEffect(() => {
    // Обновляем html lang
    document.documentElement.lang = htmlLang;
    
    // Обновляем title
    document.title = pageTitle;
    
    // Функция для обновления или создания мета-тега
    const updateOrCreateMeta = (nameOrProperty: string, content: string, isProperty: boolean = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${nameOrProperty}"]`) as HTMLMetaElement | null;
      if (meta) {
        meta.content = content;
      } else {
        meta = document.createElement('meta');
        meta.setAttribute(attr, nameOrProperty);
        meta.content = content;
        document.head.appendChild(meta);
      }
    };
    
    // Функция для обновления link тега
    const updateOrCreateLink = (rel: string, href: string, additionalAttr?: { name: string; value: string }) => {
      let selector = `link[rel="${rel}"]`;
      if (additionalAttr) {
        selector += `[${additionalAttr.name}="${additionalAttr.value}"]`;
      }
      let link = document.querySelector(selector) as HTMLLinkElement | null;
      if (link) {
        link.href = href;
      } else {
        link = document.createElement('link');
        link.rel = rel;
        link.href = href;
        if (additionalAttr) {
          link.setAttribute(additionalAttr.name, additionalAttr.value);
        }
        document.head.appendChild(link);
      }
    };
    
    // Primary Meta Tags
    updateOrCreateMeta('description', pageDescription);
    updateOrCreateMeta('author', t.headerTitle);
    
    // Open Graph
    updateOrCreateMeta('og:type', type, true);
    updateOrCreateMeta('og:url', canonicalUrl, true);
    updateOrCreateMeta('og:title', pageTitle, true);
    updateOrCreateMeta('og:description', pageDescription, true);
    updateOrCreateMeta('og:image', ogImageUrl, true);
    updateOrCreateMeta('og:image:alt', seoSettings?.ogImageTitle || pageTitle, true);
    updateOrCreateMeta('og:locale', ogLocale, true);
    updateOrCreateMeta('og:site_name', t.headerTitle, true);
    
    // Twitter
    updateOrCreateMeta('twitter:card', 'summary_large_image');
    updateOrCreateMeta('twitter:url', canonicalUrl);
    updateOrCreateMeta('twitter:title', pageTitle);
    updateOrCreateMeta('twitter:description', pageDescription);
    updateOrCreateMeta('twitter:image', ogImageUrl);
    updateOrCreateMeta('twitter:image:alt', seoSettings?.ogImageTitle || pageTitle);
    
    // Favicon
    updateOrCreateLink('icon', faviconUrl);
    
    // Canonical
    updateOrCreateLink('canonical', canonicalUrl);
    
    // Hreflang
    updateOrCreateLink('alternate', `${siteUrl}${path}`, { name: 'hreflang', value: 'en' });
    updateOrCreateLink('alternate', `${siteUrl}/ua${path === '/' ? '' : path}`, { name: 'hreflang', value: 'uk' });
    updateOrCreateLink('alternate', `${siteUrl}/ru${path === '/' ? '' : path}`, { name: 'hreflang', value: 'ru' });
    updateOrCreateLink('alternate', `${siteUrl}${path}`, { name: 'hreflang', value: 'x-default' });
    
  }, [htmlLang, pageTitle, pageDescription, canonicalUrl, ogImageUrl, faviconUrl, ogLocale, t.headerTitle, siteUrl, path, type, seoSettings?.ogImageTitle]);

  // Generate alternate language links for hreflang
  const alternateLinks = [
    { lang: 'en', href: `${siteUrl}${path}` },
    { lang: 'uk', href: `${siteUrl}/ua${path === '/' ? '' : path}` },
    { lang: 'ru', href: `${siteUrl}/ru${path === '/' ? '' : path}` },
  ];

  // JSON-LD: WebApplication
  const webApplicationData = webAppSchema.name ? {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: webAppSchema.name || t.headerTitle,
    url: canonicalUrl,
    applicationCategory: webAppSchema.applicationCategory || 'DesignApplication',
    operatingSystem: webAppSchema.operatingSystem || 'All',
    description: webAppSchema.description || pageDescription,
    browserRequirements: webAppSchema.browserRequirements || 'Requires JavaScript',
    inLanguage: htmlLang,
    offers: {
      '@type': 'Offer',
      price: webAppSchema.price || '0',
      priceCurrency: webAppSchema.priceCurrency || 'USD',
    },
    image: ogImageUrl,
  } : null;

  // JSON-LD: Organization
  const logoUrl = orgSchema.logoUrl 
    ? (orgSchema.logoUrl.startsWith('http') ? orgSchema.logoUrl : `${siteUrl}${orgSchema.logoUrl}`)
    : (brandingSettings?.logoUrl ? `${siteUrl}${brandingSettings.logoUrl}` : null);

  const organizationData = orgSchema.name ? {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: orgSchema.name || t.headerTitle,
    url: siteUrl,
    ...(logoUrl && { logo: logoUrl }),
  } : null;

  return (
    <Helmet>
      {/* Язык документа */}
      <html lang={htmlLang} />

      {/* Favicon */}
      <link rel="icon" href={faviconUrl} />

      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="author" content={t.headerTitle} />
      <meta name="robots" content="index, follow" />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Alternate Languages (hreflang) */}
      {alternateLinks.map((link) => (
        <link key={link.lang} rel="alternate" hrefLang={link.lang} href={link.href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}${path}`} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:alt" content={seoSettings?.ogImageTitle || pageTitle} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:site_name" content={t.headerTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={seoSettings?.ogImageTitle || pageTitle} />

      {/* JSON-LD Structured Data */}
      {webApplicationData && (
        <script type="application/ld+json">
          {JSON.stringify(webApplicationData)}
        </script>
      )}
      {organizationData && (
        <script type="application/ld+json">
          {JSON.stringify(organizationData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;